/** Statuses that mean a part is no longer available for sale from the vehicle */
export const UNAVAILABLE_STATUSES: ReadonlyArray<string> = ['C', 'D', 'S', 'T']

export interface TeardownTemplateComponent {
	inventoryTypeId: number
	/** Decimal string from the API. A component with a quantity of N produces N slots. */
	quantity: string | number | null
	rank: number | null
	integral: boolean | null
}

export interface TeardownPartLike {
	inventoryId: number
	storeId: number
	inventoryTypeId: number
	status: string
}

export interface TeardownPartItem<P extends TeardownPartLike> {
	kind: 'part'
	key: string
	inventoryTypeId: number
	/** Sort position, inherited from the template slot this part fills (null if it didn't fill one) */
	rank: number | null
	/** True if the template slot this part fills is flagged as integral */
	integral: boolean
	part: P
}

export interface TeardownSlotItem {
	kind: 'slot'
	key: string
	inventoryTypeId: number
	rank: number | null
	integral: boolean
}

export type TeardownItem<P extends TeardownPartLike> = TeardownPartItem<P> | TeardownSlotItem

export const partKey = (part: Pick<TeardownPartLike, 'storeId' | 'inventoryId'>) =>
	`part:${part.storeId}:${part.inventoryId}`
export const slotKey = (inventoryTypeId: number, index: number) => `slot:${inventoryTypeId}:${index}`

/** How many slots a template component makes. Always at least one, so a component with a blank or zero quantity still shows up. */
export function getSlotCount(quantity: TeardownTemplateComponent['quantity']): number {
	const parsed = typeof quantity === 'string' ? parseFloat(quantity) : quantity
	if (parsed === null || parsed === undefined || !Number.isFinite(parsed)) {
		return 1
	}
	return Math.max(1, Math.ceil(parsed))
}

const compareRank = (a: number | null, b: number | null) =>
	(a ?? Number.MAX_SAFE_INTEGER) - (b ?? Number.MAX_SAFE_INTEGER)

/**
 * Merges the parts on a vehicle with the slots of a breakdown template into one list.
 *
 * - Each template component makes `quantity` slots, in template rank order.
 * - Each existing part (other than deleted ones) fills the first open slot for its inventory type, and takes on that slot's rank and integral flag.
 * - Deleted parts never fill a slot, so their slot stays open for a replacement.
 * - Parts that don't fill a slot are still listed.
 * - `hideUnavailable` only affects which parts are listed; a sold part still counts as filling its slot, so the slot doesn't come back as empty.
 * - The part with the key `keepKey` is always listed, so hiding unavailable parts can't take away the part that's being edited.
 *
 * Parts and open slots are listed together by rank. On a tie, parts come before open slots, parts are ordered by sku, and open slots keep the template's order.
 */
export function buildTeardownItems<P extends TeardownPartLike>(
	parts: ReadonlyArray<P>,
	components: ReadonlyArray<TeardownTemplateComponent>,
	{ hideUnavailable = false, keepKey }: { hideUnavailable?: boolean; keepKey?: string } = {},
): Array<TeardownItem<P>> {
	// `rank` ties are broken by the order of the template, which Array.prototype.sort keeps stable
	const sortedComponents = [...components].sort((a, b) => compareRank(a.rank, b.rank))

	const openSlots: Array<{ inventoryTypeId: number; rank: number | null; integral: boolean; index: number }> = []
	const slotCounts = new Map<number, number>()
	for (const component of sortedComponents) {
		for (let i = 0; i < getSlotCount(component.quantity); i++) {
			const index = slotCounts.get(component.inventoryTypeId) ?? 0
			slotCounts.set(component.inventoryTypeId, index + 1)
			openSlots.push({
				inventoryTypeId: component.inventoryTypeId,
				rank: component.rank,
				integral: !!component.integral,
				index,
			})
		}
	}

	const partItems: Array<TeardownPartItem<P>> = []
	for (const part of parts) {
		let rank: number | null = null
		let integral = false
		if (part.status !== 'D') {
			const slotIndex = openSlots.findIndex(slot => slot.inventoryTypeId === part.inventoryTypeId)
			if (slotIndex > -1) {
				const [slot] = openSlots.splice(slotIndex, 1)
				rank = slot.rank
				integral = slot.integral
			}
		}
		const key = partKey(part)
		if (hideUnavailable && UNAVAILABLE_STATUSES.includes(part.status) && key !== keepKey) {
			continue
		}
		partItems.push({
			kind: 'part',
			key,
			inventoryTypeId: part.inventoryTypeId,
			rank,
			integral,
			part,
		})
	}
	partItems.sort((a, b) => compareRank(a.rank, b.rank) || a.part.inventoryId - b.part.inventoryId)

	const slotItems: Array<TeardownSlotItem> = openSlots.map(slot => ({
		kind: 'slot',
		key: slotKey(slot.inventoryTypeId, slot.index),
		inventoryTypeId: slot.inventoryTypeId,
		rank: slot.rank,
		integral: slot.integral,
	}))

	// Stable, so ties keep parts ahead of slots, and each in the order they're already in
	return [...partItems, ...slotItems].sort((a, b) => compareRank(a.rank, b.rank))
}

/** The template that should be selected when the screen opens: the one marked as default, otherwise none */
export function getDefaultTemplateId(templates: ReadonlyArray<{ id: number; default: boolean }>): number | null {
	return templates.find(template => template.default)?.id ?? null
}
